import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  FileCheck,
  Plus,
  Search,
  Filter,
  Building2,
  Loader2,
  ExternalLink,
  Settings
} from 'lucide-react';
import api, { adminQuotesAPI } from '../../services/api';
import { formatMoney } from '../../utils/format';

// Status badge styling - uses CW status names directly
const statusBadges = {
  // Standard CW opportunity statuses
  draft: { label: 'Draft', className: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300' },
  open: { label: 'Open', className: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' },
  viewed: { label: 'Viewed', className: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' },
  'accepted from portal': { label: 'Accepted from Portal', className: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300' },
  'rejected from portal': { label: 'Rejected from Portal', className: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300' },
  won: { label: 'Won', className: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300' },
  lost: { label: 'Lost', className: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300' },
  inactive: { label: 'Inactive', className: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400' },
  closed: { label: 'Closed', className: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300' },
  'on hold': { label: 'On Hold', className: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300' },
  proposed: { label: 'Proposed', className: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' },
  expired: { label: 'Expired', className: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400' },
};

// Helper to get badge for any status
const getStatusBadge = (status) => {
  const normalized = (status || '').toLowerCase();
  return statusBadges[normalized] || { label: status || 'Unknown', className: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300' };
};

export default function AdminQuotesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const lastRefreshTimestamp = useRef(null);

  // Initialize state from URL params
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [companyFilter, setCompanyFilter] = useState(searchParams.get('company') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [showCwOpportunities, setShowCwOpportunities] = useState(true);
  const [companies, setCompanies] = useState([]);

  // Update URL when filters change
  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', page.toString());
    if (search) newParams.set('search', search);
    if (statusFilter) newParams.set('status', statusFilter);
    if (companyFilter) newParams.set('company', companyFilter);
    setSearchParams(newParams, { replace: true });
  }, [page, search, statusFilter, companyFilter]);

  // Fetch companies on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await api.get('/admin/companies?limit=500');
        setCompanies(response.data.companies || []);
      } catch (err) {
        console.error('Failed to fetch companies:', err);
      }
    };
    fetchCompanies();
  }, []);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-quotes', page, search, statusFilter, companyFilter, showCwOpportunities],
    queryFn: () => adminQuotesAPI.list({
      page,
      limit: 25,
      search: search || undefined,
      status: statusFilter || undefined,
      companyId: companyFilter || undefined,
      includeOpportunities: showCwOpportunities
    }),
  });

  // Auto-refresh when returning from detail page - restore filters from URL and refetch
  useEffect(() => {
    const refreshTimestamp = location.state?.refresh;
    if (refreshTimestamp && refreshTimestamp !== lastRefreshTimestamp.current) {
      lastRefreshTimestamp.current = refreshTimestamp;

      // Restore filter states from URL params
      const urlPage = parseInt(searchParams.get('page')) || 1;
      const urlSearch = searchParams.get('search') || '';
      const urlStatus = searchParams.get('status') || '';
      const urlCompany = searchParams.get('company') || '';

      setPage(urlPage);
      setSearch(urlSearch);
      setSearchInput(urlSearch);
      setStatusFilter(urlStatus);
      setCompanyFilter(urlCompany);

      // React Query will refetch due to queryKey change from state updates
      setTimeout(() => refetch(), 0);
    }
  }, [location.state?.refresh, searchParams]);

  const quotes = data?.data?.quotes || [];
  const cwOpportunities = data?.data?.cwOpportunities || [];
  const pagination = data?.data?.pagination || { page: 1, pages: 1, total: 0 };
  const opportunityStatuses = data?.data?.opportunityStatuses || [];
  const statusCounts = data?.data?.statusCounts || { quotes: {}, opportunities: {}, totalQuotes: 0, totalOpportunities: 0 };

  // Combine quotes and CW opportunities into a unified list
  const allItems = [
    ...quotes.map(q => ({ ...q, itemType: 'quote' })),
    ...cwOpportunities.map(o => ({ ...o, itemType: 'opportunity' }))
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Handle date-only strings (YYYY-MM-DD) by adding time to avoid timezone shift
    const date = dateString.includes('T')
      ? new Date(dateString)
      : new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return formatMoney(parseFloat(price || 0));
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Quote Management</h1>
          <p className="text-neutral-600 dark:text-neutral-400">Create and manage customer quotes</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/admin/quotes/settings')}
            className="flex items-center gap-2 px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 font-medium transition-colors"
          >
            <Settings className="w-5 h-5" />
            Settings
          </button>
          <button
            onClick={() => navigate('/admin/quotes/new')}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            New Quote
          </button>
        </div>
      </div>

      {/* Stats - Using CW statuses directly */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        {/* Totals */}
        <button
          onClick={() => { setStatusFilter(''); setPage(1); }}
          className={`bg-white dark:bg-neutral-800 rounded-lg border p-4 text-left hover:border-primary-300 dark:hover:border-primary-600 transition-colors ${statusFilter === '' ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800' : 'border-neutral-200 dark:border-neutral-700'}`}
        >
          <div className="text-2xl font-bold text-neutral-900 dark:text-white">
            {statusCounts.totalQuotes + statusCounts.totalOpportunities}
          </div>
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Total</div>
        </button>

        {/* Draft */}
        <button
          onClick={() => { setStatusFilter('draft'); setPage(1); }}
          className={`bg-white dark:bg-neutral-800 rounded-lg border p-4 text-left hover:border-primary-300 dark:hover:border-primary-600 transition-colors ${statusFilter === 'draft' ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800' : 'border-neutral-200 dark:border-neutral-700'}`}
        >
          <div className="text-2xl font-bold text-neutral-600 dark:text-neutral-300">
            {(statusCounts.quotes.Draft || statusCounts.quotes.draft || 0) + (statusCounts.opportunities.draft || 0)}
          </div>
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Draft</div>
        </button>

        {/* Open - combines local quotes and CW opportunities with Open status */}
        <button
          onClick={() => { setStatusFilter('open'); setPage(1); }}
          className={`bg-white dark:bg-neutral-800 rounded-lg border p-4 text-left hover:border-primary-300 dark:hover:border-primary-600 transition-colors ${statusFilter === 'open' ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800' : 'border-neutral-200 dark:border-neutral-700'}`}
        >
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {(statusCounts.quotes.Open || statusCounts.quotes.open || 0) + (statusCounts.opportunities.open || 0)}
          </div>
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Open</div>
        </button>

        {/* Viewed */}
        <button
          onClick={() => { setStatusFilter('viewed'); setPage(1); }}
          className={`bg-white dark:bg-neutral-800 rounded-lg border p-4 text-left hover:border-primary-300 dark:hover:border-primary-600 transition-colors ${statusFilter === 'viewed' ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800' : 'border-neutral-200 dark:border-neutral-700'}`}
        >
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {(statusCounts.quotes.Viewed || statusCounts.quotes.viewed || 0) + (statusCounts.opportunities.viewed || 0)}
          </div>
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Viewed</div>
        </button>

        {/* Accepted from Portal */}
        <button
          onClick={() => { setStatusFilter('accepted from portal'); setPage(1); }}
          className={`bg-white dark:bg-neutral-800 rounded-lg border p-4 text-left hover:border-primary-300 dark:hover:border-primary-600 transition-colors ${statusFilter === 'accepted from portal' ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800' : 'border-neutral-200 dark:border-neutral-700'}`}
        >
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {(statusCounts.quotes['Accepted from Portal'] || statusCounts.quotes['accepted from portal'] || 0) + (statusCounts.opportunities['accepted from portal'] || 0)}
          </div>
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Accepted</div>
        </button>

        {/* Rejected - combines Rejected from Portal + Lost */}
        <button
          onClick={() => { setStatusFilter('rejected'); setPage(1); }}
          className={`bg-white dark:bg-neutral-800 rounded-lg border p-4 text-left hover:border-primary-300 dark:hover:border-primary-600 transition-colors ${statusFilter === 'rejected' ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800' : 'border-neutral-200 dark:border-neutral-700'}`}
        >
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
            {(statusCounts.quotes['Rejected from Portal'] || statusCounts.quotes['rejected from portal'] || 0) +
             (statusCounts.opportunities['rejected from portal'] || 0) +
             (statusCounts.opportunities.lost || 0)}
          </div>
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Rejected</div>
        </button>

        {/* Won */}
        <button
          onClick={() => { setStatusFilter('won'); setPage(1); }}
          className={`bg-white dark:bg-neutral-800 rounded-lg border p-4 text-left hover:border-primary-300 dark:hover:border-primary-600 transition-colors ${statusFilter === 'won' ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800' : 'border-neutral-200 dark:border-neutral-700'}`}
        >
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {(statusCounts.quotes.Won || statusCounts.quotes.won || 0) + (statusCounts.opportunities.won || 0)}
          </div>
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Won</div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                placeholder="Search by name, doc #, or company..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
              />
            </div>
          </form>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
            <select
              value={companyFilter}
              onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }}
              className="border border-neutral-300 dark:border-neutral-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 max-w-[200px] bg-white dark:bg-neutral-700 dark:text-white"
            >
              <option value="">All Companies</option>
              {companies.map((company) => (
                <option key={company.cw_company_id} value={company.cw_company_id}>
                  {company.name}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="border border-neutral-300 dark:border-neutral-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
            >
              <option value="">All Statuses</option>
              {/* Use CW opportunity statuses directly */}
              {opportunityStatuses.map(s => (
                <option key={s.id} value={s.name.toLowerCase()}>{s.name}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
              <input
                type="checkbox"
                checked={showCwOpportunities}
                onChange={(e) => { setShowCwOpportunities(e.target.checked); setPage(1); }}
                className="rounded border-neutral-300 dark:border-neutral-600 text-primary-500 focus:ring-primary-500 dark:bg-neutral-700"
              />
              Include CW Opportunities
            </label>
            {(search || statusFilter || companyFilter) && (
              <button
                onClick={() => {
                  setSearch('');
                  setSearchInput('');
                  setStatusFilter('');
                  setCompanyFilter('');
                  setPage(1);
                }}
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Quotes Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-neutral-500 dark:text-neutral-400">Loading quotes...</p>
          </div>
        ) : allItems.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">
            <FileCheck className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
            <p>No quotes found</p>
            {(search || statusFilter) && (
              <p className="text-sm mt-2">Try adjusting your filters</p>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-neutral-50 dark:bg-neutral-700/50 border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Quote</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Company</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Source</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Date</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Total</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-neutral-500 dark:text-neutral-400">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                  {allItems.map((item) => {
                    const badge = getStatusBadge(item.status);
                    const isOpportunity = item.itemType === 'opportunity';

                    return (
                      <tr
                        key={item.id}
                        onClick={() => {
                          sessionStorage.setItem('adminQuotesReturnUrl', window.location.pathname + window.location.search);
                          if (isOpportunity) {
                            navigate(`/quotes/${item.cwOpportunityId}?from=admin`);
                          } else {
                            navigate(`/admin/quotes/${item.id}/view`);
                          }
                        }}
                        className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-neutral-900 dark:text-white">{item.quoteName}</div>
                          {item.qwDocNumber && (
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">#{item.qwDocNumber}</div>
                          )}
                          {isOpportunity && (
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">CW #{item.cwOpportunityId}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-neutral-400" />
                            <span className="text-neutral-900 dark:text-white">{item.companyName || '-'}</span>
                          </div>
                          {item.contactName && (
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">{item.contactName}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isOpportunity ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300">
                              <ExternalLink className="w-3 h-3" />
                              ConnectWise
                            </span>
                          ) : item.source === 'connectwise' ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300">
                              CW Synced
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                              Portal
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">
                          {isOpportunity
                            ? formatDate(item.expectedCloseDate)
                            : formatDate(item.quoteDate)
                          }
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-neutral-900 dark:text-white">
                          {formatPrice(item.total)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.className}`}>
                            {badge.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-4 py-3 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
                <div className="text-sm text-neutral-500 dark:text-neutral-400">
                  Showing {(pagination.page - 1) * 25 + 1} to {Math.min(pagination.page * 25, pagination.total)} of {pagination.total} quotes
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-neutral-300 dark:border-neutral-600 rounded text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                    disabled={page >= pagination.pages}
                    className="px-3 py-1 border border-neutral-300 dark:border-neutral-600 rounded text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
